import type { CatalogChoice } from '../catalog-languages';
import type { SourceConfig } from '@viva/core';
import type { RelayConfig } from '../network';
import type { DeviceIdentity } from './device';
import { t } from '../i18n';
import type { MessageKey } from '../i18n/en';
import { en } from '../i18n/en';

export interface AccountInfo {
  /** Generated username, or the email address for accounts created in the app. */
  username: string;
  email: string | null;
  whatsapp: string | null;
  kind: 'none' | 'trial' | 'paid';
  plan: 'standard' | 'gold';
  clientName: string;
  /** End of the trial or subscription (ms), null when open-ended. */
  expiresAt: number | null;
  /** Streams (and devices) allowed at the same time. */
  maxStreams: number;
  /** The owner's own account: no end date, any devices. */
  admin?: boolean;
}

export interface Playlist {
  name: string;
  config: SourceConfig;
}

/** What the app shows: the channels, or a screen explaining why not and what to do. */
export type Access = 'active' | 'verify-email' | 'no-plan' | 'trial-pending' | 'trial-used' | 'trial-ended' | 'expired';

export interface SessionData {
  account: AccountInfo;
  access: Access;
  /** Present only while access is active. */
  playlist: Playlist | null;
  /** Trial on the shared pool: streams wait for a free line. */
  pooled: boolean;
  /** Browser version: the streaming relay and its pass (renewed with each session check). */
  relay: RelayConfig | null;
  /** The languages of the channel list; null on the line pool means "ask the viewer first". */
  catalog: CatalogChoice | null;
  /** Languages to propose (the viewer's country and phone country). */
  suggestedLanguages: string[];
  trialHours: number;
  support: { whatsapp: string };
  /** Admin's "Stop 4K through the relay" (#301). Missing: off. */
  uhdRelayCut?: boolean;
  /** Server clock at the time of the answer, to correct a wrong device clock. */
  serverTime: number;
}

export type ApiResult<T> = { ok: true; data: T } | { ok: false; code: string; message: string };

/**
 * Account server address. The web version is served by the account server
 * itself, so it uses its own address; app builds (TV, iPhone, Mac) set
 * VITE_ACCOUNT_API at build time. In development it defaults to the local
 * server (`wrangler dev` on port 8788) on the same host as the page, so a
 * phone on the LAN reaches it too.
 */
export function accountApiBase(): string | null {
  const configured = import.meta.env.VITE_ACCOUNT_API;
  if (configured) return configured.replace(/\/+$/, '');
  if (typeof location === 'undefined') return null;
  if (import.meta.env.DEV) return `${location.protocol}//${location.hostname}:8788`;
  return location.protocol === 'https:' || location.protocol === 'http:' ? location.origin : null;
}

/** A failure in the app's language: the server's known error codes are translated, others keep its English text. */
function failure(code: string, serverMessage?: string): { ok: false; code: string; message: string } {
  const key = `error.${code}`;
  return { ok: false, code, message: key in en ? t(key as MessageKey) : (serverMessage ?? t('error.server-error')) };
}

async function call<T>(path: string, init: RequestInit): Promise<ApiResult<T>> {
  const base = accountApiBase();
  if (!base) return failure('not-configured');
  let response: Response;
  try {
    response = await fetch(base + path, { ...init, headers: { 'content-type': 'application/json', ...init.headers } });
  } catch {
    return failure('offline');
  }
  const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!response.ok) return failure(body.error ?? 'server-error', body.message);
  return { ok: true, data: body as T };
}

export function login(username: string, password: string, device: DeviceIdentity): Promise<ApiResult<SessionData & { token: string }>> {
  return call('/v1/login', { method: 'POST', body: JSON.stringify({ username, password, device }) });
}

export interface SignupInput {
  email: string;
  whatsapp: string;
  password: string;
  name: string;
}

const SOURCE_KEY = 'viva.source';
const SOURCE_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref', 'click'];

/**
 * Where this visitor first came from (the website's buttons and ads add utm_* to the app link), kept from the
 * first visit until they sign up, for the admin's statistics.
 */
export function rememberSource(): void {
  const params = new URLSearchParams(location.search);
  try {
    if (!localStorage.getItem(SOURCE_KEY)) {
      const source = Object.fromEntries(SOURCE_KEYS.map((key) => [key, params.get(key) ?? '']).filter(([, value]) => value));
      const referrer = document.referrer ? new URL(document.referrer).hostname : '';
      if (!source.ref && referrer && referrer !== location.hostname) source.ref = referrer;
      if (Object.keys(source).length > 0) localStorage.setItem(SOURCE_KEY, JSON.stringify(source));
    }
  } catch {
    // No storage: the sign-up has no source.
  }
  // The address bar shows only the app's address (owner's choice), without the link's tags.
  if (SOURCE_KEYS.some((key) => params.has(key))) {
    for (const key of SOURCE_KEYS) params.delete(key);
    const rest = params.toString();
    history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  }
}

function storedSource(): Record<string, string> | undefined {
  try {
    return JSON.parse(localStorage.getItem(SOURCE_KEY) ?? 'null') ?? undefined;
  } catch {
    return undefined;
  }
}

export function signup(input: SignupInput, device: DeviceIdentity): Promise<ApiResult<SessionData & { token: string }>> {
  const language = typeof navigator === 'undefined' ? '' : navigator.language;
  return call('/v1/signup', { method: 'POST', body: JSON.stringify({ ...input, device, source: storedSource(), language }) });
}

/** A search the viewer typed, for the admin's statistics. */
export function reportSearch(token: string, search: string): Promise<ApiResult<unknown>> {
  return call('/v1/activity/search', { method: 'POST', headers: authorized(token), body: JSON.stringify({ search }) });
}

/** The visitor's country code as the server sees it ("MA"), or an empty string. */
export function visitorCountry(): Promise<ApiResult<{ country: string }>> {
  return call('/v1/country', { method: 'GET' });
}

const authorized = (token: string) => ({ authorization: `Bearer ${token}` });

export function verifyEmail(token: string, code: string): Promise<ApiResult<SessionData>> {
  return call('/v1/verify-email', { method: 'POST', headers: authorized(token), body: JSON.stringify({ code }) });
}

export function resendCode(token: string): Promise<ApiResult<unknown>> {
  return call('/v1/resend-code', { method: 'POST', headers: authorized(token), body: '{}' });
}

export function requestPasswordReset(email: string): Promise<ApiResult<unknown>> {
  return call('/v1/password-reset', { method: 'POST', body: JSON.stringify({ email }) });
}

export function confirmPasswordReset(email: string, code: string, password: string): Promise<ApiResult<unknown>> {
  return call('/v1/password-reset/confirm', { method: 'POST', body: JSON.stringify({ email, code, password }) });
}

/** A stream slot, or a place in the trial pool's queue. `ticket`: relay pass for the assigned pool line. */
export type PlaybackSlot =
  | { queued?: false; full?: false; leaseId: string; maxStreams: number; ticket?: string | null; expiresAt?: number }
  | { queued: true; full?: false; position: number; etaMinutes: number; etaSeconds?: number }
  /** All of the plan's screens are playing: the viewer picks one to stop (`stop` on the next start). */
  | { queued?: false; full: true; maxStreams: number; screens: PlayingScreen[] };

/** One of the account's screens now playing. */
export interface PlayingScreen {
  leaseId: string;
  /** E.g. "web · SamsungBrowser/30 · Linux". */
  device: string;
  title: string;
  since: number;
}

/** What a screen is playing, shown to the admin ("Watching now"). */
export interface Watching {
  title: string;
  kind: 'live' | 'movie' | 'series';
  /** Whether a picture is showing, so the admin can see streams that don't work. */
  status?: 'loading' | 'playing' | 'buffering' | 'paused' | 'error' | 'waiting' | 'ended' | 'no-picture' | 'no-sound';
  /** Live channels: the stream URL, so viewers of one channel share a pool line. */
  stream?: string;
}

/** `replaces`: this screen's current stream, on a zap (line pool). `stop`: the screen to stop when all are playing. */
export function startPlayback(token: string, watching?: Watching, replaces?: string, stop?: string): Promise<ApiResult<PlaybackSlot>> {
  return call('/v1/playback/start', { method: 'POST', headers: authorized(token), body: JSON.stringify({ ...watching, replaces, stop }) });
}

export function playbackHeartbeat(token: string, leaseId: string, watching?: Watching): Promise<ApiResult<{ ok: boolean; reason?: string; ticket?: string | null }>> {
  return call('/v1/playback/heartbeat', { method: 'POST', headers: authorized(token), body: JSON.stringify({ leaseId, ...watching }) });
}

/** A channel airing a match, tested by the relay and shown only while it plays well. */
export interface SportsChannel {
  streamId: number;
  label: string;
  logo: string | null;
  language: string | null;
  quality: 'UHD' | 'FHD' | 'HD' | 'SD' | null;
  /** Also listed for another match at the same time: it may show that one (missing from older servers). */
  shared?: boolean;
  /** Viewers confirmed it shows the match (#156). */
  confirmed?: boolean;
  /** Screens playing it now: joining shares their connection, so it starts at once (#13). */
  watching?: number;
  /** Set by the app: the same channel in other qualities, best picture first, for the player's quality menu. */
  variants?: ChannelVariant[];
}

/** One quality of a match's channel ("4K", "FHD", "HD", "" when the name says none). */
export interface ChannelVariant {
  streamId: number;
  label: string;
  quality: string;
}

export interface SportsMatch {
  id: string;
  home: string;
  away: string;
  competition: string;
  /** The league heading it is listed under ('' when unknown; missing from older servers). */
  league?: string;
  /** The league's popularity rank, 0 first; null when not ranked. */
  leagueRank?: number | null;
  startsAt: number | null;
  live: boolean;
  /** On now, but its channels are still being tested. */
  checking: boolean;
  /** Team pictures (flag or crest), null when unknown. */
  homeCrest: string | null;
  awayCrest: string | null;
  /** Live score and minute when known; `at` is when it was fetched (the minute counts on from there). */
  liveScore: { home: number | null; away: number | null; status: string; minute: number | null; extra: number | null; at: number } | null;
  channels: SportsChannel[];
  /**
   * Channels that may show it but aren't playable from here (not passed the check): before kick-off
   * where it will be on; once live where it might be, with the stream so the viewer can try it (missing from older servers).
   */
  /** `shared`: one of a broadcaster's numbered channels; which one has the match isn't known yet (#156). */
  upcoming?: { label: string; language: string | null; streamId?: number; logo?: string | null; quality?: SportsChannel['quality']; shared?: boolean }[];
  /** Live, and none of its channels is playable or being checked: only names to search (older servers: absent). */
  failed?: boolean;
  /** From the schedule of the coming days: no channels yet, they are tested on match day (#93). */
  scheduled?: boolean;
  /** A famous club's match FotMob doesn't list: maybe a youth game or friendly, not the first team (#127). */
  unconfirmed?: boolean;
  /** For this viewer: how worth watching it is (missing from older servers). */
  score?: number;
}

/** A ranked league's match of the coming days (#93), without channels. */
export interface ScheduledMatch {
  id: string;
  home: string;
  away: string;
  competition: string;
  league: string;
  leagueRank: number;
  startsAt: number;
  /** For this viewer, as today's matches. */
  score: number;
  homeCrest: string | null;
  awayCrest: string | null;
  /** Today's coming matches: channels it is expected on, not tested yet (#111; missing from older servers). */
  upcoming?: { label: string; language: string | null }[];
}

export interface SportsToday {
  /** The provider the channels belong to; null when the account has no line pool. */
  serverUrl: string | null;
  /** The home screen's slider: this viewer's best matches (by where they are and their phone's country). */
  featured: string[];
  /** Commentary languages to offer first for this viewer. */
  languages: string[];
  matches: SportsMatch[];
  /** Whether today has a match worth the slider; without one Home shows the coming days' big matches (missing from older servers). */
  bigToday?: boolean;
  /** The ranked leagues' matches of the coming days, by kick-off (missing from older servers). */
  schedule?: ScheduledMatch[];
}

export function getSports(token: string): Promise<ApiResult<SportsToday>> {
  return call('/v1/sports', { method: 'GET', headers: authorized(token) });
}

/** Saves the languages of the viewer's channel list. */
/** Whether to ask this viewer if the match is on the channel they play (#156). */
export function sportsQuestion(token: string, matchId: string, streamId: number): Promise<ApiResult<{ ask: boolean }>> {
  return call('/v1/sports/question', { method: 'POST', headers: authorized(token), body: JSON.stringify({ matchId, streamId }) });
}

/** The viewer's answer: is the match on this channel (#156). */
export function sportsVote(token: string, matchId: string, streamId: number, yes: boolean): Promise<ApiResult<{ ok: boolean }>> {
  return call('/v1/sports/vote', { method: 'POST', headers: authorized(token), body: JSON.stringify({ matchId, streamId, yes }) });
}

export function setCatalogLanguages(token: string, choice: CatalogChoice): Promise<ApiResult<SessionData>> {
  return call('/v1/catalog-languages', { method: 'POST', headers: authorized(token), body: JSON.stringify(choice) });
}

export function stopPlayback(token: string, leaseId: string): Promise<ApiResult<unknown>> {
  return call('/v1/playback/stop', { method: 'POST', headers: authorized(token), body: JSON.stringify({ leaseId }) });
}

export function requestTrial(token: string, device: DeviceIdentity): Promise<ApiResult<SessionData>> {
  return call('/v1/trial-request', { method: 'POST', headers: { authorization: `Bearer ${token}` }, body: JSON.stringify({ device }) });
}

export function deleteAccount(token: string, password: string): Promise<ApiResult<unknown>> {
  return call('/v1/delete-account', { method: 'POST', headers: { authorization: `Bearer ${token}` }, body: JSON.stringify({ password }) });
}

export function checkSession(token: string): Promise<ApiResult<SessionData>> {
  return call('/v1/session', { method: 'GET', headers: { authorization: `Bearer ${token}` } });
}

export function logout(token: string): Promise<ApiResult<unknown>> {
  return call('/v1/logout', { method: 'POST', headers: { authorization: `Bearer ${token}` } });
}
